import { ThemeSchemaResponse } from '@api/api/theme-overview';
import { PieChart } from '@design/charts/pie-chart/PieChart';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { useThemeDirectionSplit } from '@terminal/hooks/use-theme-direction-split/useThemeDirectionSplit';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './DirectionSplit.module.scss';

export const DirectionSplit = ({
  theme,
  isCompact = false,
}: {
  theme: ThemeSchemaResponse;
  isCompact?: boolean;
}) => {
  const { t } = useTranslation(['widget', 'common']);
  const { themeName } = useChartTheme();

  const { data, isFetching, isError } = useThemeDirectionSplit({
    themeId: theme.id,
  });

  if (isFetching) {
    return (
      <SkeletonLoader
        className={styles.skeletonLoaderWrapper}
        areas={[
          {
            styles: {
              width: '100%',
              height: isCompact ? '204px' : '100%',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (isError || !data) {
    throw new Error('Direction Split: api error');
  }

  const themeDirections = [
    {
      name: t('widget:themeOverview.tabs.overview.directionSplit.positive'),
      value: data.positive,
      color: '--viz-bullish',
    },
    {
      name: t('widget:themeOverview.tabs.overview.directionSplit.negative'),
      value: data.negative,
      color: '--viz-bearish',
    },
    {
      name: t('common:neutral'),
      value: data.neutral,
      color: '--surface-neutral',
    },
  ].filter(direction => direction.value > 0);

  return (
    <div
      className={clsx(styles.root, {
        [styles.compact]: isCompact,
      })}
      data-testid="direction-split"
    >
      <div className={styles.titleWrapper}>
        <div className={styles.sectionTitle}>
          <SectionTitle
            title={t('widget:themeOverview.tabs.overview.directionSplit.title')}
            variant={isCompact ? 'small' : 'default'}
          />
        </div>
        <div className={styles.subtitleLabel}>
          {t('widget:themeOverview.tabs.overview.directionSplit.subtitle', {
            count: data.total,
          })}
        </div>
      </div>
      <div className={styles.pieChartWrapper} data-pie-chart-wrapper>
        <PieChart
          theme={themeName}
          data={themeDirections}
          disableAnimation={!isCompact}
        />
      </div>
    </div>
  );
};
